import React, { useState, useEffect } from 'react';
import { X, Cookie, Settings } from 'lucide-react';
import Button from './Button';

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      // Show banner after a short delay
      setTimeout(() => setIsVisible(true), 1000);
    } else {
      const savedPreferences = JSON.parse(consent);
      setPreferences(savedPreferences);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true
    };
    saveConsent(allAccepted);
  };

  const handleAcceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false
    };
    saveConsent(necessaryOnly);
  };

  const handleSavePreferences = () => {
    saveConsent(preferences);
    setShowSettings(false);
  };

  const saveConsent = (prefs) => {
    localStorage.setItem('cookieConsent', JSON.stringify(prefs));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setIsVisible(false);

    // TODO: Initialize analytics/marketing scripts based on preferences
    if (prefs.analytics) {
      // Initialize analytics (e.g., Google Analytics, Plausible)
      console.log('Analytics enabled');
    }
    if (prefs.marketing) {
      // Initialize marketing scripts
      console.log('Marketing enabled');
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-slide-up">
      <div className="max-w-6xl mx-auto">
        <div className="bg-white rounded-2xl shadow-hover border border-primary-gray-border overflow-hidden">
          {!showSettings ? (
            // Main Cookie Banner
            <div className="p-6 md:p-8">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
                {/* Icon */}
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 rounded-xl bg-primary-blue-light flex items-center justify-center">
                    <Cookie className="w-7 h-7 text-primary-blue-mid" />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-grow">
                  <h3 className="text-xl font-semibold text-primary-text-dark mb-2">
                    Cookies & Datenschutz
                  </h3>
                  <p className="text-sm text-primary-text-muted leading-relaxed">
                    Wir verwenden Cookies, um Ihnen die beste Erfahrung auf unserer Website zu bieten.
                    Notwendige Cookies sind für die Funktionalität erforderlich. Mit Ihrer Zustimmung
                    verwenden wir auch Analytics-Cookies zur Verbesserung unserer Website.{' '}
                    <a href="/datenschutz" className="text-primary-blue-mid hover:underline">
                      Mehr erfahren
                    </a>
                  </p>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={Settings}
                    onClick={() => setShowSettings(true)}
                    className="w-full sm:w-auto"
                  >
                    Einstellungen
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleAcceptNecessary}
                    className="w-full sm:w-auto"
                  >
                    Nur Notwendige
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleAcceptAll}
                    className="w-full sm:w-auto"
                  >
                    Alle akzeptieren
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            // Cookie Settings Panel
            <div className="p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-primary-text-dark">
                  Cookie-Einstellungen
                </h3>
                <button
                  onClick={() => setShowSettings(false)}
                  className="p-2 hover:bg-primary-gray-light rounded-lg transition-colors"
                  aria-label="Schließen"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="flex items-start justify-between p-4 bg-primary-gray-light rounded-lg">
                  <div className="flex-grow pr-4">
                    <h4 className="font-semibold text-primary-text-dark mb-1">
                      Notwendige Cookies
                    </h4>
                    <p className="text-sm text-primary-text-muted">
                      Diese Cookies sind für die Grundfunktionen der Website erforderlich
                      und können nicht deaktiviert werden.
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={true}
                      disabled
                      className="w-5 h-5 rounded border-primary-gray-border"
                    />
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-start justify-between p-4 bg-white border border-primary-gray-border rounded-lg">
                  <div className="flex-grow pr-4">
                    <h4 className="font-semibold text-primary-text-dark mb-1">
                      Analytics Cookies
                    </h4>
                    <p className="text-sm text-primary-text-muted">
                      Helfen uns zu verstehen, wie Besucher mit unserer Website interagieren,
                      um sie zu verbessern.
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences({
                        ...preferences,
                        analytics: e.target.checked
                      })}
                      className="w-5 h-5 rounded border-primary-gray-border"
                    />
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-start justify-between p-4 bg-white border border-primary-gray-border rounded-lg">
                  <div className="flex-grow pr-4">
                    <h4 className="font-semibold text-primary-text-dark mb-1">
                      Marketing Cookies
                    </h4>
                    <p className="text-sm text-primary-text-muted">
                      Werden verwendet, um Besuchern relevante Werbung und Marketingkampagnen
                      anzuzeigen.
                    </p>
                  </div>
                  <div className="flex-shrink-0">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) => setPreferences({
                        ...preferences,
                        marketing: e.target.checked
                      })}
                      className="w-5 h-5 rounded border-primary-gray-border"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  variant="secondary"
                  onClick={() => setShowSettings(false)}
                  className="w-full sm:w-auto"
                >
                  Abbrechen
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSavePreferences}
                  className="w-full sm:flex-grow"
                >
                  Einstellungen speichern
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
